const DEFAULT_TRIAL_DAYS = 30;
const DEFAULT_TRIAL_TIME_ZONE = "America/New_York";
const MS_PER_DAY = 24 * 60 * 60 * 1000;

export const TRIAL_STAGE = Object.freeze({
  NONE: "none",
  NEW: "new",
  ACTIVE: "active",
  LATE: "late",
  EXPIRING: "expiring",
  EXPIRED: "expired",
});

export function normalizeDateValue(value) {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value.toDate === "function") {
    const date = value.toDate();
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (typeof value.toMillis === "function") {
    const date = new Date(value.toMillis());
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (typeof value === "object") {
    const seconds = typeof value.seconds === "number"
      ? value.seconds
      : typeof value._seconds === "number"
        ? value._seconds
        : null;
    if (seconds != null) {
      const nanoseconds = typeof value.nanoseconds === "number"
        ? value.nanoseconds
        : typeof value._nanoseconds === "number"
          ? value._nanoseconds
          : 0;
      const date = new Date(seconds * 1000 + nanoseconds / 1000000);
      return Number.isNaN(date.getTime()) ? null : date;
    }
  }

  if (typeof value === "number") {
    const date = new Date(value > 1e12 ? value : value * 1000);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (typeof value === "string") {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  return null;
}

function isValidTimeZone(timeZone) {
  if (!timeZone) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone }).format();
    return true;
  } catch {
    return false;
  }
}

export function getTrialTimeZone(user) {
  const capturedTimeZone = String(
    user?.countryAttribution?.browserTimezone || ""
  ).trim();
  return isValidTimeZone(capturedTimeZone)
    ? capturedTimeZone
    : DEFAULT_TRIAL_TIME_ZONE;
}

function getZonedDateTimeParts(date, timeZone) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);
  const values = Object.fromEntries(parts.map(({ type, value }) => [type, value]));
  return {
    year: Number(values.year),
    month: Number(values.month),
    day: Number(values.day),
    hour: Number(values.hour),
    minute: Number(values.minute),
    second: Number(values.second),
  };
}

function addCalendarDays({ year, month, day }, days) {
  const result = new Date(Date.UTC(year, month - 1, day + days));
  return {
    year: result.getUTCFullYear(),
    month: result.getUTCMonth() + 1,
    day: result.getUTCDate(),
  };
}

function getUtcDateParts(date) {
  return {
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
  };
}

function localMidnightToDate({ year, month, day }, timeZone) {
  const targetTime = Date.UTC(year, month - 1, day);
  let candidate = new Date(targetTime);

  for (let attempt = 0; attempt < 3; attempt += 1) {
    const actual = getZonedDateTimeParts(candidate, timeZone);
    const actualAsUtc = Date.UTC(
      actual.year,
      actual.month - 1,
      actual.day,
      actual.hour,
      actual.minute,
      actual.second
    );
    candidate = new Date(candidate.getTime() + targetTime - actualAsUtc);
  }

  return candidate;
}

export function resolveTrialEndDate(user, { trialDays = DEFAULT_TRIAL_DAYS } = {}) {
  if (!user) return null;

  const timeZone = getTrialTimeZone(user);
  const explicitTrialEndDate = normalizeDateValue(user.newTrialEndDate);
  if (explicitTrialEndDate) {
    return localMidnightToDate(
      addCalendarDays(getUtcDateParts(explicitTrialEndDate), 1),
      timeZone
    );
  }

  const accountCreatedDate = normalizeDateValue(user.accountCreated);
  if (!accountCreatedDate) return null;

  const createdLocalDate = getZonedDateTimeParts(accountCreatedDate, timeZone);
  return localMidnightToDate(addCalendarDays(createdLocalDate, trialDays), timeZone);
}

export function getTrialDaysRemaining(user, { now = new Date(), trialDays = DEFAULT_TRIAL_DAYS } = {}) {
  const trialEndDate = resolveTrialEndDate(user, { trialDays });
  if (!trialEndDate) return null;

  const nowDate = normalizeDateValue(now) || new Date();
  return Math.max(0, Math.ceil((trialEndDate.getTime() - nowDate.getTime()) / MS_PER_DAY));
}

export function isTrialExpired(user, options = {}) {
  const trialEndDate = resolveTrialEndDate(user, options);
  if (!trialEndDate) return user?.trial === false && user?.premium !== true;

  const nowDate = normalizeDateValue(options.now) || new Date();
  return trialEndDate.getTime() <= nowDate.getTime();
}

export function getTrialStage(user, { now = new Date(), trialDays = DEFAULT_TRIAL_DAYS } = {}) {
  if (!user?.trial) {
    return isTrialExpired(user, { now, trialDays }) ? TRIAL_STAGE.EXPIRED : TRIAL_STAGE.NONE;
  }

  const trialEndDate = resolveTrialEndDate(user, { trialDays });
  if (!trialEndDate) return TRIAL_STAGE.ACTIVE;

  const nowDate = normalizeDateValue(now) || new Date();
  const daysRemaining = getTrialDaysRemaining(user, { now: nowDate, trialDays });
  if (trialEndDate.getTime() <= nowDate.getTime()) return TRIAL_STAGE.EXPIRED;
  if (daysRemaining <= 3) return TRIAL_STAGE.EXPIRING;
  if (daysRemaining <= 7) return TRIAL_STAGE.LATE;

  const accountCreatedDate = normalizeDateValue(user.accountCreated);
  if (accountCreatedDate) {
    const accountAgeDays = Math.floor((nowDate.getTime() - accountCreatedDate.getTime()) / MS_PER_DAY);
    if (accountAgeDays <= 7) return TRIAL_STAGE.NEW;
  }

  return TRIAL_STAGE.ACTIVE;
}

export function formatMonthDayOrdinal(value, fallback = "a future date") {
  const date = normalizeDateValue(value);
  if (!date) return fallback;

  const day = date.getDate();
  const month = date.toLocaleDateString("en-US", { month: "long" });
  const ordinalSuffix =
    day >= 11 && day <= 13
      ? "th"
      : day % 10 === 1
        ? "st"
        : day % 10 === 2
          ? "nd"
          : day % 10 === 3
            ? "rd"
            : "th";

  return `${month} ${day}${ordinalSuffix}`;
}

export function isOnActiveFreeTrial(user, options = {}) {
  if (!user) return false;
  if (user.premium) return false;
  if (!user.trial) return false;
  if (isTrialExpired(user, options)) return false;
  if ((user.schoolLicenseType || "").toLowerCase() === "pilot") return false;
  return true;
}

export function formatTrialDaysLabel(daysRemaining) {
  if (daysRemaining === 1) return "1 day";
  return `${daysRemaining} days`;
}

export function getTrialStatusPresentation(user, options = {}) {
  const campaignPresentation = options.getCampaignPresentation?.(user, options);
  if (campaignPresentation) return campaignPresentation;

  if (!isOnActiveFreeTrial(user, options)) return null;

  const daysRemaining = getTrialDaysRemaining(user, options);
  if (daysRemaining == null) return null;

  const stage = getTrialStage(user, options);
  const trialEndDate = resolveTrialEndDate(user, options);
  const endDateLabel = formatMonthDayOrdinal(trialEndDate);
  const daysLabel = formatTrialDaysLabel(daysRemaining);
  const pillLabel = `${daysLabel} left in Free Trial`;
  const pillCompactLabel = daysRemaining === 1 ? "1d" : `${daysRemaining}d`;

  let headline;
  let message;
  let tone = "default";

  switch (stage) {
    case TRIAL_STAGE.NEW:
      headline = "Free trial active";
      message = "No credit card on file - you won't be charged unless you subscribe.";
      break;
    case TRIAL_STAGE.ACTIVE:
      headline = `${daysLabel} left`;
      message = "Subscribe anytime to keep your automations running.";
      break;
    case TRIAL_STAGE.LATE:
      headline = `${daysLabel} left`;
      message = `Trial ends ${endDateLabel}. Subscribe to keep access.`;
      break;
    case TRIAL_STAGE.EXPIRING:
      tone = "urgent";
      if (daysRemaining === 1) {
        headline = "Last day of your trial";
        message = "Subscribe today to keep access after your trial ends.";
      } else {
        headline = `${daysLabel} left`;
        message = `Trial ends ${endDateLabel}. Your content is saved. Subscribe to keep access.`;
      }
      break;
    case TRIAL_STAGE.NONE:
    case TRIAL_STAGE.EXPIRED:
      return null;
    default: {
      const unhandledStage = stage;
      void unhandledStage;
      return null;
    }
  }

  return {
    daysRemaining,
    stage,
    headline,
    message,
    pillLabel,
    pillCompactLabel,
    ctaLabel: "View plans",
    tone,
  };
}
